import React, { useState } from 'react';

interface PricingPlansSectionProps {
    highlightColor: {
        primary: string;
        hover: string;
        gradient: {
            from: string;
            to: string;
            fromLight: string;
            toGray: string;
        };
    };
}

interface Plan {
    name: string;
    price?: number | string; // Optional - if not provided, shows "Contact for pricing"
    priceLabel?: string; // Optional custom label like "per month", "per year", etc.
    customPriceText?: string; // Optional - completely custom price display like "Contact for pricing"
    description: string;
    features: string[];
    buttonText?: string;
}

interface CustomerType {
    id: string;
    label: string;
    plans: Plan[];
}

const PricingPlansSection: React.FC<PricingPlansSectionProps> = ({ highlightColor }) => {
    const [selectedType, setSelectedType] = useState(0);

    // Customizable pricing configuration
    const customerTypes: CustomerType[] = [
        {
            id: 'sports-teams',
            label: 'Sports Teams',
            plans: [
                {
                    name: 'Core',
                    price: 'Free',
                    description: 'For small teams starting to unify athlete data.',
                    features: [
                        'Up to 10 athletes',
                        'Connect basic wearables (WHOOP, Garmin, Oura)',
                        'Simple readiness and recovery trends',
                        'Standard team dashboard',
                        'Email support'
                    ],
                    buttonText: 'Start Core Plan'
                },
                {
                    name: 'Performance',
                    price: 500,
                    priceLabel: '/month',
                    description: 'For full rosters and performance staffs.',
                    features: [
                        'Up to 60 athletes',
                        'Automated reports & readiness alerts',
                        'Team-specific insight models',
                        'Multi-sport dashboards',
                        'Priority support',
                        'Data cleaning & quality filters',
                    ],
                    buttonText: 'Start Performance Plan'
                },
                {
                    name: 'Pro',
                    price: 'Contact for pricing',
                    description: 'For programs requiring custom integration & scaling.',
                    features: [
                        'Unlimited athletes',
                        'Custom model tuned to your training system',
                        'Dedicated onboarding & white-glove support',
                        'API and testing data integrations',
                        'Administrative controls',
                        'Compliance-ready environment',
                    ],
                    buttonText: 'Contact Sales'
                }
            ]
        },
        {
            id: 'pt-clinics',
            label: 'PT Clinics',
            plans: [
                {
                    name: 'Core',
                    price: 'Free',
                    description: 'For clinics wanting simple client data visibility.',
                    features: [
                        'Up to 5 active clients',
                        'Connect wearables across clients',
                        'Basic readiness/recovery view',
                        'Progress snapshots',
                        'Email support'
                    ],
                    buttonText: 'Start Core Plan'
                },
                {
                    name: 'Performance',
                    price: 150,
                    priceLabel: '/month',
                    description: 'For clinics offering hybrid or long-term client monitoring.',
                    features: [
                        'Up to 50 clients',
                        'Automated check-ins & weekly summaries',
                        'Trend monitoring (load, adherence, recovery)',
                        'Client-specific insight models',
                        'Priority support',
                        'Enhanced data cleaning',
                    ],
                    buttonText: 'Start Performance Plan'
                },
                {
                    name: 'Pro',
                    price: 'Contact for pricing',
                    description: 'For clinics delivering high-end rehab & performance care.',
                    features: [
                        'Unlimited clients',
                        'Custom insight model tuned to your clinical method',
                        'Dedicated onboarding for staff',
                        'Scheduling + notes system integrations',
                        'Multi-clinician collaboration',
                        'White-glove support',
                    ],
                    buttonText: 'Contact Sales'
                }
            ]
        },
        {
            id: 'gyms-trainers',
            label: 'Gyms & Trainers',
            plans: [
                {
                    name: 'Core',
                    price: 'Free',
                    description: 'For coaches training a small client roster.',
                    features: [
                        'Up to 3 clients',
                        'Wearable aggregation',
                        'Basic readiness/recovery snapshots',
                        'Single-coach dashboard',
                        'Email support',
                    ],
                    buttonText: 'Start Core Plan'
                },
                {
                    name: 'Performance',
                    price: 100,
                    priceLabel: '/month',
                    description: 'For serious coaches scaling their client base.',
                    features: [
                        'Up to 30 clients',
                        'Weekly automated reports',
                        'Client-specific training insights',
                        'Progress tracking dashboards',
                        'Priority support',
                    ],
                    buttonText: 'Start Performance Plan'
                },
                {
                    name: 'Pro',
                    price: 'Contact for pricing',
                    description: 'For performance gyms and multi-coach facilities.',
                    features: [
                        'Unlimited clients',
                        'Custom insights aligned to your training system',
                        'Multi-coach collaboration & admin controls',
                        'Dedicated onboarding',
                        'White-glove facility support',
                    ],
                    buttonText: 'Contact Sales'
                }
            ]
        }
    ];

    const currentPlans = customerTypes[selectedType].plans;

    return (
        <section className="pt-32 pb-36 px-4 sm:px-6 lg:px-8 bg-black relative notebook-grid overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-white/10 to-transparent pointer-events-none"></div>
            <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white/10 to-transparent pointer-events-none"></div>
            <div className="max-w-7xl mx-auto relative z-10">
                <div className="max-w-7xl mx-auto pb-12 border-b border-neutral-800 relative z-10 text-center">
                    <h1 className="text-4xl md:text-5xl font-bold text-white mb-8 observe-fade opacity-0">
                        Pricing
                    </h1>
                    <p className="text-gray-300 observe-fade opacity-0">
                        Flexible pricing plans designed for teams of all sizes. All plans include AI-powered insights, real-time monitoring, and dedicated support.
                    </p>
                </div>
                <div className="text-center mb-12 observe-fade opacity-0 mt-12">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Choose your plan</h2>
                    
                    {/* Customer Type Toggle */}
                    <div className="flex items-center justify-center">
                        <div
                            className="relative inline-flex h-12 items-center gap-0 p-1 transition-all duration-300 border border-neutral-700"
                            style={{
                                backgroundColor: 'rgb(38, 38, 38)',
                            }}
                        >
                            {customerTypes.map((type, index) => (
                                <button
                                    key={type.id}
                                    onClick={() => setSelectedType(index)}
                                    className="relative flex-1 px-4 md:px-6 py-2 focus:outline-none transition-all duration-200"
                                    aria-label={`Select ${type.label}`}
                                >
                                    {/* Hover background - shows when not selected */}
                                    <span
                                        className={`absolute inset-0 h-full transition-all duration-300 ease-in-out ${
                                            selectedType === index ? 'opacity-0' : 'opacity-0 hover:opacity-20'
                                        }`}
                                        style={{
                                            backgroundColor: 'white'
                                        }}
                                    />
                                    {/* Sliding background highlight */}
                                    <span
                                        className={`absolute inset-0 h-full transition-all duration-300 ease-in-out ${
                                            selectedType === index ? 'opacity-100' : 'opacity-0'
                                        }`}
                                        style={{
                                            backgroundColor: 'white'
                                        }}
                                    />
                                    <span
                                        className={`relative z-10 text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                                            selectedType === index ? 'text-black' : 'text-gray-400 hover:text-gray-200'
                                        }`}
                                    >
                                        {type.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {currentPlans.map((plan, index) => (
                        <div 
                            key={index} 
                            className="bg-neutral-900 p-8 observe-fade opacity-0 transition-all duration-300 hover:shadow-xl flex flex-col grid-accent"
                        >
                            <h3 className="text-2xl font-bold text-white mb-4">{plan.name}</h3>
                            <div className="mb-4">
                                {plan.customPriceText ? (
                                    <span className="text-2xl font-bold text-white"
                                    >
                                        {plan.customPriceText}
                                    </span>
                                ) : (
                                    <>
                                        <span className="text-4xl font-bold text-white"
                                        >
                                            {typeof plan.price === 'number' ? `$${plan.price.toLocaleString()}` : plan.price}
                                        </span>
                                        {plan.priceLabel && <span className="text-gray-400">{plan.priceLabel}</span>}
                                    </>
                                )}
                            </div>
                            <p className="text-gray-300 mb-4 min-h-[3rem]">{plan.description}</p>
                            <ul className="space-y-3 mb-8 flex-grow">
                                {plan.features.map((feature, idx) => (
                                    <li key={idx} className="flex items-start text-gray-300">
                                        <svg 
                                            className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                            style={{ color: highlightColor.primary }}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>
                            {/* <button 
                                className="w-full py-3 text-white transition-all duration-300 font-medium hover:shadow-lg mt-auto"
                                style={{ backgroundColor: highlightColor.primary }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.hover;
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = highlightColor.primary;
                                    e.currentTarget.style.transform = 'translateY(0)';
                                }}
                            >
                                {plan.buttonText || `Get Started`}
                            </button> */}
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

export default PricingPlansSection;

